import { useCallback, useState } from "react";

import { type AttributionFilter, type MetadataCloud } from "@doitintl/cmp-models";
import { Box, TextField } from "@mui/material";

import { type MetadataOption } from "../../../types";
import FilterDialog from "./FilterDialog";
import { FilterModeOption } from "./types";
import { getMode } from "./utils";

type Props = {
  onSave: (filter: AttributionFilter) => void;
  dimension: MetadataOption;
  cloudProviders?: MetadataCloud[];
  valuesOnStart?: string[];
};

export const FilterDialogContainer = ({ dimension, onSave, cloudProviders }: Props) => {
  const [open, setOpen] = useState(false);

  const handleSave = useCallback(
    (mode: FilterModeOption, filtersOrRegex: string[] | string, inverse: boolean) => {
      const isRegexp = mode === FilterModeOption.MATCHES_REGEX || mode === FilterModeOption.DOES_NOT_MATCHES_REGEX;
      if (!dimension?.id) {
        return;
      }

      let regexp: string | null = null;
      let values: string[] | null = null;
      if (typeof filtersOrRegex === "string" && isRegexp) {
        regexp = filtersOrRegex;
      } else if (Array.isArray(filtersOrRegex) && !isRegexp) {
        values = [...filtersOrRegex];
      }

      const filter: AttributionFilter = {
        id: dimension.id,
        values,
        regexp,
        inverse,
        allowNull: false,
        field: dimension.data.field,
        key: dimension.data.key,
        type: dimension.data.type,
        mode: getMode(mode),
      };

      if (dimension.data.nullFallback) {
        const nullIndex = filter.values?.findIndex((v) => v === dimension.data.nullFallback);
        if (nullIndex !== undefined && nullIndex !== -1) {
          filter.allowNull = true;
          filter.values?.splice(nullIndex, 1);
        }
      }
      onSave(filter);
    },
    [dimension, onSave]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Box>
      <TextField
        value={dimension?.data.label}
        variant="outlined"
        fullWidth
        sx={{
          width: 220,
          "& .MuiInputBase-input": {
            cursor: "pointer",
          },
        }}
        InputProps={{
          readOnly: true,
        }}
        onClick={handleOpen}
      />
      {open && (
        <FilterDialog
          selected={dimension}
          open={open}
          onClose={handleClose}
          onSave={handleSave}
          cloudProviders={cloudProviders}
        />
      )}
    </Box>
  );
};
